import React from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/useToast';
import { useCollaboratorByIdQuery, useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import type { Solicitacao } from '../../../models';
import { usePreCadastroAdmissaoQuery } from '../../../hooks/data';
import {
  CHAVES_CPF_PRE_CADASTRO,
  CHAVES_ENDERECO_PRE_CADASTRO,
  enderecoParaEntrega,
  formatarEndereco,
  respostaPreCadastro,
} from '../../../utils/enderecoContato';

interface ColaboradorCabecalhoInfoProps {
  solicitacao: Solicitacao;
  /** CPF/endereço só pra SI/RH — mesmo gate da seção de Pré-cadastro (dado sensível). */
  mostrarDadosSensiveis: boolean;
}

function DadoCopiavel({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  const toast = useToast();
  if (!valor) {
    return (
      <span>
        <span className="font-medium text-gray-700">{rotulo}:</span> <span className="text-gray-400">não informado</span>
      </span>
    );
  }
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(valor);
      toast.success(`${rotulo} copiado`);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-medium text-gray-700">{rotulo}:</span> {valor}
      <button type="button" onClick={copiar} className="text-gray-400 hover:text-gray-600" title={`Copiar ${rotulo}`}>
        <ClipboardDocumentIcon className="h-4 w-4" />
      </button>
    </span>
  );
}

/**
 * Linha do colaborador no cabeçalho da Solicitação — sempre com cargo/senioridade e, pra
 * SI/RH, CPF e endereço completo (com botão de copiar — é o que o SI precisa na hora de
 * gerar o código dos Correios, sem abrir o perfil). Fonte dos dados:
 * - colaborador já vinculado: perfil do Colaborador (cargo/senioridade/CPF/endereço);
 * - Admissão ainda sem vínculo: cargo/senioridade informados na abertura
 *   (`colaboradorPendente`) e CPF/endereço das respostas do Pré-cadastro, se houver.
 */
export default function ColaboradorCabecalhoInfo({ solicitacao, mostrarDadosSensiveis }: ColaboradorCabecalhoInfoProps) {
  const pendente = solicitacao.colaboradorId == null ? solicitacao.colaboradorPendente : null;
  // Cargo vem do cadastro público (qualquer pessoa lê — solicitante também vê). Senioridade,
  // CPF e endereço só existem no perfil completo (collaborators_private), que exige role de
  // colaboradores (ver firestore.rules) — só busca pra SI/RH, sem gerar erro de permissão
  // pra quem não pode ler. Sem acesso, esses campos só não aparecem/ficam "não informado".
  const { data: colaboradoresPublicos = [] } = useCollaboratorsPublicQuery();
  const colaboradorPublico = colaboradoresPublicos.find((c) => c.id === solicitacao.colaboradorId) ?? null;
  const { data: colaborador } = useCollaboratorByIdQuery(
    mostrarDadosSensiveis ? (solicitacao.colaboradorId ?? undefined) : undefined
  );
  const { data: preCadastro } = usePreCadastroAdmissaoQuery(
    pendente && mostrarDadosSensiveis ? solicitacao.preCadastroId : null
  );

  const nome = pendente?.nome ?? solicitacao.colaboradorNome;
  const cargoSenioridade = [
    pendente ? pendente.positionName : (colaboradorPublico?.positionName ?? colaborador?.positionName),
    pendente ? pendente.seniorityName : colaborador?.seniorityName,
  ]
    .filter(Boolean)
    .join(' - ');

  const cpf = pendente ? respostaPreCadastro(preCadastro, CHAVES_CPF_PRE_CADASTRO) : colaborador?.cpf ?? null;
  const endereco = pendente
    ? respostaPreCadastro(preCadastro, CHAVES_ENDERECO_PRE_CADASTRO)
    : formatarEndereco(enderecoParaEntrega(colaborador?.addresses));

  return (
    <div className="text-sm text-gray-500">
      <p>
        {[nome, pendente ? solicitacao.colaboradorEmail : null, cargoSenioridade].filter(Boolean).join(' · ')}
      </p>
      {mostrarDadosSensiveis && (nome || solicitacao.colaboradorId) && (
        <p className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
          <DadoCopiavel rotulo="CPF" valor={cpf} />
          <DadoCopiavel rotulo="Endereço" valor={endereco} />
          {/* Só no Desligamento — depois do corte de acessos o e-mail da Raro deixa de
              existir, e o SI precisa falar com a pessoa pelo pessoal. */}
          {solicitacao.tipoSolicitacao === 'desligamento' && (
            <DadoCopiavel rotulo="E-mail pessoal" valor={colaborador?.personalEmail ?? null} />
          )}
        </p>
      )}
    </div>
  );
}
